/* eslint-disable import/prefer-default-export */

export interface ServiceDisplay {
  text: string;
  icon?: any;
}

// Pet Care Services
const petCareServicesMap: Record<string, ServiceDisplay> = {
  ORAL_MEDICATION: { text: 'Can administer oral medication' },
  INJECTED_MEDICATION: { text: 'Can administer injected medication' },
  DOG_CPR: { text: 'Can administer dog CPR' },
  DOG_FIRST_AID: { text: 'Can administer dog first aid' },
};

// Child Care Services
const childCareServicesMap: Record<string, ServiceDisplay> = {
  HOMEWORK_HELP: { text: 'Homework help' },
  VALID_DRIVERS_LICENSE: { text: "Valid driver's license" },
  WILLING_TO_DRIVE_CHILDREN: { text: 'Can drive kids' },
  WILLING_TO_RUN_ERRANDS: { text: 'Can run errands' },
  COMFORTABLE_WITH_PETS: { text: 'Works with pets' },
  SICK_CHILDREN: { text: 'Sick care' },
  INFANT_CARE: { text: 'Infant care' },
  SPECIAL_NEEDS: { text: 'Special needs care' },
  TWINS: { text: 'Twins experience' },
  MEAL_PREPARATION: { text: 'Prepare snacks/meals' },
  LAUNDRY: { text: 'Laundry' },
  LIGHT_HOUSEKEEPING: { text: 'Housekeeping' },
  SWIMMING_SUPERVISION: { text: 'Swimming supervision' },
  CRAFTS: { text: 'Crafts' },
  POTTY_TRAINING: { text: 'Toilet training' },
  TRAVEL_WITH_FAMILY: { text: 'Travel with family' },
  ACCESS_TO_TRANSPORTATION: { text: 'Has a car' },
};

// Companion Care Services
const companionCareServicesMap: Record<string, ServiceDisplay> = {
  WILLING_TO_RUN_ERRANDS: { text: 'Can run errands' },
  COMFORTABLE_WITH_PETS: { text: 'Works with pets' },
  WILLING_TO_PROVIDE_TRANSPORTATION: { text: 'Willing to provide non-medical transportation' },
  ORAL_MEDICATION: { text: 'Can administer oral medication' },
  INJECTED_MEDICATION: { text: 'Can administer injected medication' },
  MEDICATION_REMINDERS: { text: 'Medication reminders' },
  MOBILITY_ASSISTANCE: { text: 'Mobility assistance' },
  MEAL_PREPARATION: { text: 'Meal preparation' },
  LIGHT_HOUSEKEEPING: { text: 'Light housekeeping' },
  MEDICATION_ADMINISTRATION: { text: 'Medication administration' },
  MEDICATION_ASSISTANCE: { text: 'Medication assistance' },
  MEDICATION_MANAGEMENT: { text: 'Medication management' },
  GROOMING_GUIDANCE: { text: 'Grooming guidance' },
  ASSISTANCE_WITH_PETS: { text: 'Assistance with pets' },
  MEAL_PLANNING: { text: 'Meal planning' },
  NUTRITION_EDUCATION: { text: 'Nutrition education' },
  MOBILITY_TRAINING: { text: 'Mobility training' },
  WOUND_CARE: { text: 'Wound care' },
  PAIN_MANAGEMENT: { text: 'Pain management' },
  IV_THERAPY_INJECTIONS: { text: 'IV therapy/injections' },
  DIABETIC_CARE: { text: 'Diabetic care' },
  CENTRAL_LINE_CARE: { text: 'Central line care' },
  OSTOMY_CARE: { text: 'Ostomy care' },
};

// Infant Care Services
const infantCareServicesMap: Record<string, ServiceDisplay> = {
  INSTRUCTION: { text: 'Instruction (proper swaddling, feeding, changing, bathing)' },
  SETTING_ROUTINE: { text: 'Assist with setting a routine' },
  OCCASIONAL_CARE: { text: 'Occasional care' },
  MOTHERS_HELPER: { text: "Mother's helper" },
  OVERNIGHT_CARE: { text: 'Overnight care' },
};

// Housekeeping Services
const housekeepingServicesMap: Record<string, ServiceDisplay> = {
  GENERAL_CLEANING: { text: 'General cleaning' },
  DEEP_CLEANING: { text: 'Deep cleaning' },
  LAUNDRY: { text: 'Laundry' },
  DISHES: { text: 'Dishes' },
  ORGANIZATION: { text: 'Organization' },
  CLEANING: { text: 'Cleaning' },
  ERRANDS: { text: 'Errands' },
  MAINTENANCE: { text: 'Maintenance' },
  COMFORTABLE_WITH_PETS: { text: 'Works with pets' },
};

export type PetCareServiceKey = keyof typeof petCareServicesMap;
export type ChildCareServiceKey = keyof typeof childCareServicesMap;
export type InfantCareServiceKey = keyof typeof infantCareServicesMap;
export type CompanionCareServiceKey = keyof typeof companionCareServicesMap;
export type HousekeepingServiceKey = keyof typeof housekeepingServicesMap;

export const serviceMap = {
  petCare: petCareServicesMap,
  childCare: childCareServicesMap,
  infantCare: infantCareServicesMap,
  companionCare: companionCareServicesMap,
  housekeeping: housekeepingServicesMap,
} as const;

export interface PetCareServices {
  qualifications?: PetCareServiceKey[];
}

export interface ChildCareServices {
  homeworkHelp?: boolean;
  validDriversLicense?: boolean;
  willingToDriveChildren?: boolean;
  willingToRunErrands?: boolean;
  comfortableWithPets?: boolean;
  sickChildren?: boolean;
  infantCare?: boolean;
  infantCareExperience?: string;
  infantCareServices?: InfantCareServiceKey[];
  specialNeeds?: boolean;
  twins?: boolean;
  accessToTransportation?: boolean;
  additionalServices?: ChildCareServiceKey[];
}

export interface CompanionCareServices {
  willingToRunErrands?: boolean;
  comfortableWithPets?: boolean;
  willingToProvideTransportation?: boolean;
  individualizedServices?: CompanionCareServiceKey[];
  healthAndMedicalServices?: CompanionCareServiceKey[];
}

export interface HousekeepingServices {
  comfortableWithPets?: boolean;
  typesOfServices?: HousekeepingServiceKey[];
}

export type Services = {
  petCare?: PetCareServices;
  childCare?: ChildCareServices;
  babysitter?: ChildCareServices;
  nanny?: ChildCareServices;
  companionCare?: CompanionCareServices;
  housekeeping?: HousekeepingServices;
};

export function getServiceFields(profileData: Record<string, any>, careType: string): Services {
  const {
    homeworkHelp,
    validDriversLicense,
    willingToDriveChildren,
    willingToRunErrands,
    comfortableWithPets,
    sickChildren,
    infantCare,
    infantCareExperience,
    infantCareServices,
    specialNeeds,
    twins,
    accessToTransportation,
    additionalServices,
    qualifications,
    willingToProvideTransportation,
    individualizedServices,
    healthAndMedicalServices,
    typesOfServices,
  } = profileData;

  switch (careType) {
    case 'petCare':
      return { petCare: { qualifications } };
    case 'babysitter':
    case 'nanny':
      return {
        [careType]: {
          homeworkHelp,
          validDriversLicense,
          willingToDriveChildren,
          willingToRunErrands,
          comfortableWithPets,
          sickChildren,
          infantCare,
          infantCareExperience,
          infantCareServices,
          specialNeeds,
          twins,
          accessToTransportation,
          additionalServices,
        },
      };
    case 'companionCare':
      return {
        companionCare: {
          willingToRunErrands,
          comfortableWithPets,
          willingToProvideTransportation,
          individualizedServices,
          healthAndMedicalServices,
        },
      };
    case 'housekeeping':
      return {
        housekeeping: {
          comfortableWithPets,
          typesOfServices,
        },
      };
    default:
      return {};
  }
}

export function getCareTypeServices(profileData: Record<string, any>, careType: string): any {
  const services = getServiceFields(profileData, careType);
  return services[careType];
}

export function hasServices(services: Services, careType: string): boolean {
  if (!services) return false;

  switch (careType) {
    case 'petCare':
      return !!services.petCare?.qualifications?.length;
    case 'babysitter':
    case 'nanny': {
      const childCareServices = services[careType];
      const hasChildCareServices = !!(
        childCareServices?.homeworkHelp ||
        childCareServices?.validDriversLicense ||
        childCareServices?.willingToDriveChildren ||
        childCareServices?.willingToRunErrands ||
        childCareServices?.comfortableWithPets ||
        childCareServices?.sickChildren ||
        childCareServices?.infantCare ||
        childCareServices?.infantCareExperience ||
        childCareServices?.specialNeeds ||
        childCareServices?.twins ||
        childCareServices?.accessToTransportation ||
        childCareServices?.additionalServices?.length
      );
      const hasInfantCareServices = !!(childCareServices?.infantCareServices?.length || childCareServices?.infantCareExperience);
      return hasChildCareServices || hasInfantCareServices;
    }
    case 'companionCare':
      return !!(
        services.companionCare?.willingToRunErrands ||
        services.companionCare?.comfortableWithPets ||
        services.companionCare?.willingToProvideTransportation ||
        services.companionCare?.individualizedServices?.length ||
        services.companionCare?.healthAndMedicalServices?.length
      );
    case 'housekeeping':
      return !!(services.housekeeping?.comfortableWithPets || services.housekeeping?.typesOfServices?.length);
    default:
      return false;
  }
}

export function mapPetCareServices(services: PetCareServices): ServiceDisplay[] {
  if (!services?.qualifications) return [];
  return services.qualifications.map((key: string) => serviceMap.petCare[key]).filter(Boolean);
}

export function mapChildCareServices(services: ChildCareServices): ServiceDisplay[] {
  if (!services) return [];
  const items: ServiceDisplay[] = [];
  const map = serviceMap.childCare;

  if (services.homeworkHelp) items.push(map.HOMEWORK_HELP);
  if (services.validDriversLicense) items.push(map.VALID_DRIVERS_LICENSE);
  if (services.willingToDriveChildren) items.push(map.WILLING_TO_DRIVE_CHILDREN);
  if (services.willingToRunErrands) items.push(map.WILLING_TO_RUN_ERRANDS);
  if (services.comfortableWithPets) items.push(map.COMFORTABLE_WITH_PETS);
  if (services.sickChildren) items.push(map.SICK_CHILDREN);
  if (services.infantCare) items.push(map.INFANT_CARE);
  if (services.specialNeeds) items.push(map.SPECIAL_NEEDS);
  if (services.twins) items.push(map.TWINS);
  if (services.accessToTransportation) items.push(map.ACCESS_TO_TRANSPORTATION);

  // Add additional services if they exist
  if (services.additionalServices?.length) {
    items.push(...services.additionalServices.map((key) => map[key]).filter(Boolean));
  }

  return items;
}

export function mapInfantCareServices(services: InfantCareServiceKey[]): ServiceDisplay[] {
  if (!services?.length) return [];
  return services.map((key: string) => serviceMap.infantCare[key]).filter(Boolean);
}

export function mapCompanionCareServices(
  services: CompanionCareServices,
  category: 'general' | 'individualized' | 'health'
): ServiceDisplay[] {
  if (!services) return [];
  const map = serviceMap.companionCare;

  switch (category) {
    case 'general': {
      const items: ServiceDisplay[] = [];
      if (services.willingToRunErrands) items.push(map.WILLING_TO_RUN_ERRANDS);
      if (services.comfortableWithPets) items.push(map.COMFORTABLE_WITH_PETS);
      if (services.willingToProvideTransportation) items.push(map.WILLING_TO_PROVIDE_TRANSPORTATION);
      return items;
    }
    case 'individualized': {
      if (!services.individualizedServices) return [];
      return services.individualizedServices.map((key: string) => map[key]).filter(Boolean);
    }
    case 'health': {
      if (!services.healthAndMedicalServices) return [];
      return services.healthAndMedicalServices.map((key: string) => map[key]).filter(Boolean);
    }
    default:
      return [];
  }
}

export function mapHousekeepingServices(services: HousekeepingServices): ServiceDisplay[] {
  if (!services) return [];
  const map = serviceMap.housekeeping;
  const items: ServiceDisplay[] = [];

  if (services.comfortableWithPets) items.push(map.COMFORTABLE_WITH_PETS);
  if (services.typesOfServices?.length) {
    items.push(
      ...services.typesOfServices
        .filter((key: string) => key !== 'COMFORTABLE_WITH_PETS')
        .map((key: string) => map[key])
        .filter(Boolean)
    );
  }

  return items;
}
